/**
 * NativeSelect: the shared native <select>, drawn the same height as the
 * inputs beside it (#3684).
 *
 * WebKit (every iPhone browser, and Safari on a Mac) paints its own short menu
 * button for a select and ignores the padding and height it is given, so a
 * select styled with the same classes as an input rendered 22 px tall next to
 * a 38 px input. `appearance-none` hands the box back to CSS: the padding,
 * line height and border then apply exactly as they do on the input, and the
 * arrow WebKit stops drawing is drawn here instead.
 *
 * The box comes from the caller when a page's inputs are not the standard
 * field (BOM's compact rows): pass the neighbour input's classes as
 * `className` and the select takes its height. Layout (width, margin, flex)
 * goes on `wrapperClassName`, because the chevron is positioned against the
 * wrapper. The wrapper is a span so the select can sit inside a <label>.
 *
 * DESIGN.md: border, background, text and focus ring are semantic tokens, so
 * light and dark both hold; the chevron is `text-muted-foreground`.
 */
import type { SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";

/** The standard field box: the same classes as the app's text inputs. */
export const SELECT_BOX_CLASS =
  "w-full rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";

/** What every native select gets on top of its box: WebKit's menu button off, room for the chevron. */
export const NATIVE_SELECT_CHROME = {
  md: "appearance-none pr-9 disabled:cursor-not-allowed disabled:opacity-60",
  sm: "appearance-none pr-7 disabled:cursor-not-allowed disabled:opacity-60",
} as const;

const CHEVRON = {
  md: "right-3 h-4 w-4",
  sm: "right-2 h-3.5 w-3.5",
} as const;

export type NativeSelectSize = keyof typeof NATIVE_SELECT_CHROME;

export interface NativeSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  /** The select's box. Defaults to the standard field; pass the neighbour input's classes to match it. */
  className?: string;
  /** Layout for the wrapper: width, margin, flex. */
  wrapperClassName?: string;
  /** `sm` for text-xs rows: a smaller chevron and less room kept for it. */
  size?: NativeSelectSize;
}

export function NativeSelect({ className = SELECT_BOX_CLASS, wrapperClassName = "", size = "md", ...props }: NativeSelectProps) {
  return (
    <span className={`relative block ${wrapperClassName}`.trim()} data-native-select="">
      <select {...props} className={`${className} ${NATIVE_SELECT_CHROME[size]}`} />
      <ChevronDown
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground ${CHEVRON[size]}`}
        aria-hidden="true"
      />
    </span>
  );
}
